import React, { useState, useEffect } from "react";
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  HistoryIcon,
  CalendarIcon,
  ClipboardIcon,
  Edit3Icon,
} from "lucide-react";
import { GetJobApplicationsParams, JobApplication } from "@type/job";
import { formatHumanReadable } from "@util/date";
import { getJobApplications } from "@service/job";
import { Tabs, TabsList, TabsTrigger } from "@component/tabs";
import { MetaData } from "@type/common";
import { useNavigate } from "react-router-dom";
import { getMyContractOffers } from "@service/contract";
import { getApplicationStatusClass } from "@util/string";
import LoadingBox from "@component/loading-box";
import { CandidateInterviewStatus } from "@type/candidate";
import { getInterviewStatus } from "@service/interview";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@component/select";
import { useQuery } from "@util/useQuery";

const DEFAULT_LIMIT = 15;
const DEFAULT_PAGE = 1;

export function JobApplicationContainer() {
  const navigate = useNavigate();
  const queryParams = useQuery();
  const [metadata, setMetadata] = useState<MetaData>({
    pagination: {
      page: DEFAULT_PAGE,
      limit: DEFAULT_LIMIT,
      total: 0,
      total_pages: 0,
    },
  });
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [activeJobsTab, setActiveJobsTab] = useState("all");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingInterviewStatus, setLoadingInterviewStatus] = useState<boolean>(true);
  const [interviewStatus, setInterviewStatus] = useState<CandidateInterviewStatus | undefined>(undefined)
  const [jobApplicationParams, setJobApplicationParams] =
    useState<GetJobApplicationsParams>({
      page: DEFAULT_PAGE,
      limit: DEFAULT_LIMIT,
      application_status: activeJobsTab,
    });

  useEffect(() => {
    getInterviewStatus().then((resp) => {
      setInterviewStatus(resp.data);
      setLoadingInterviewStatus(false)
    }).catch((error) => {
      console.error('failed to get interview status', error)
      setLoadingInterviewStatus(false)
    })

    onLoadByParams();
  }, []);

  const onLoadByParams = async () => {
    const status = queryParams.get("status");
    if (status) {
      setActiveJobsTab(status);
      if (status === "all" || status === "applied" || status === "draft") {
        const params = {
          ...jobApplicationParams,
          application_status: status,
        }
        if (status === "applied") {
          params.myjob_status = "Applied";
          params.application_status = "";
        } else if (status === "draft") {
          params.application_status = "Draft";
        }
        await fetchApplications(params);
      } else {
        const resp = await getMyContractOffers(status);
        const applications: JobApplication[] = resp.map((item) => ({
          job_id: item.job_id,
          job_title: item.job_title,
          company_name: item.company_name,
          status: item.status,
          start_date: item.start_date,
          end_date: item.end_date,
          created_at: item.created_at,
        })) as JobApplication[] || [];
        setApplications(applications);
      }
    } else {
      fetchApplications(jobApplicationParams);
    }
  }

  const fetchApplications = async (
    params: GetJobApplicationsParams,
    isLoadMore = false
  ) => {
    try {
      if (isLoadMore) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      setError(null);
      const response = await getJobApplications({
        ...params,
        application_status: params.application_status !== "all" ? params.application_status : "",
      });
      if (isLoadMore) {
        setApplications((preApplication: JobApplication[]) => {
          return [...preApplication, ...response.data];
        });
      } else {
        setApplications(response.data);
      }

      setMetadata(response.meta);
    } catch (err) {
      console.error("Error fetching applications:", err);
      setError("Failed to load your applications.");
    } finally {
      if (isLoadMore) {
        setIsLoadingMore(false);
      } else {
        setIsLoading(false);
      }
    }
  };

  // Helper function to get status type based on application status
  const getStatusType = (status: string) => {
    switch (status.toLowerCase()) {
      case "hired":
      case "offer":
        return "success";
      case "rejected offer":
      case "rejected application":
        return "rejected";
      default:
        return "pending";
    }
  };

  const handleChangeTab = async (status: string) => {
    setActiveJobsTab(status);
    setJobApplicationParams({
      ...jobApplicationParams,
      application_status: status,
    });

    if (status === "all" || status === "applied" || status === "draft") {
      const params = {
        ...jobApplicationParams,
        application_status: status,
      }
      if (status === "applied") {
        params.myjob_status = "Applied";
        params.application_status = "";
      } else if (status === "draft") {
        params.application_status = "Draft";
      }
      await fetchApplications(params);
    } else {
      const resp = await getMyContractOffers(status);
      const applications: JobApplication[] = resp.map((item) => ({
        job_id: item.job_id,
        job_title: item.job_title,
        company_name: item.company_name,
        status: item.status,
        start_date: item.start_date,
        end_date: item.end_date,
        created_at: item.created_at,
      })) as JobApplication[] || [];
      setApplications(applications);
    }


    if (status === "all") {
      queryParams.delete("status");
    } else {
      queryParams.set("status", status);
    }
    const newRelativePathQuery = window.location.pathname + '?' + queryParams.toString();
    window.history.pushState(null, '', newRelativePathQuery);
  };

  // const handleLoadMore = useCallback(async () => {
  //   const nextPage = currentPage + 1;
  //   setCurrentPage(nextPage);
  //   setJobApplicationParams((prev: GetJobApplicationsParams) => {
  //     return {
  //       ...prev,
  //       page: nextPage,
  //     };
  //   });
  //   await fetchApplications(
  //     {
  //       ...jobApplicationParams,
  //       page: nextPage,
  //       limit: DEFAULT_LIMIT,
  //     },
  //     true
  //   );
  // }, [currentPage]);

  const tabOptions = [
    {
      value: "all",
      label: "All Jobs",
    },
    {
      value: "draft",
      label: "Draft",
      icon: <Edit3Icon className="h-4 w-4 mr-1" />,
    },
    {
      value: "applied",
      label: "Applied",
      icon: <ClipboardIcon className="h-4 w-4 mr-1" />,
    },
    {
      value: "ongoing",
      label: "Ongoing",
      icon: <ClockIcon className="h-4 w-4 mr-1" />,
    },
    {
      value: "past",
      label: "Past",
      icon: <HistoryIcon className="h-4 w-4 mr-1" />,
    },
    {
      value: "future",
      label: "Future",
      icon: <CalendarIcon className="h-4 w-4 mr-1" />,
    }
  ];

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">My Jobs </h2>
        <p className="text-gray-600">
          View and manage your accepted jobs.
        </p>
      </div>

      <Tabs value={activeJobsTab} className="w-full mb-6 hidden sm:block">
        <TabsList className="grid w-full grid-cols-6 mb-8">
          {tabOptions.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              onClick={() => handleChangeTab(tab.value)}
              className="flex items-center gap-2"
            >
              {tab.icon}
              <span>{tab.label}</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="flex sm:hidden mb-4">
        <Select value={activeJobsTab} onValueChange={(val: string) => handleChangeTab(val)}>
          <SelectTrigger className="w-full">
            <div className="flex items-center gap-2">
              {tabOptions.find((tab) => tab.value === activeJobsTab)?.icon}
              <span>
                {tabOptions.find((tab) => tab.value === activeJobsTab)?.label}
              </span>
            </div>
          </SelectTrigger>
          <SelectContent>
            {tabOptions.map((tab) => (
              <SelectItem key={tab.value} value={tab.value}>
                <div className="flex items-center gap-2">
                  {tab.icon}
                  <span>{tab.label}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Loading State */}
      {isLoading && (
        <LoadingBox />
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-md my-4">
          {error}
        </div>
      )}

      <div className="space-y-4">
        {applications.length > 0 ? (
          <>
            {applications.map((app, index) => {
              const today = new Date();
              const startAt = app.start_date ? new Date(app.start_date) : null;
              const endAt = app.end_date ? new Date(app.end_date) : null;

              if (activeJobsTab === "ongoing") {
                if (!(startAt &&
                  ((endAt && startAt <= today && today <= endAt) ||
                    (!endAt && startAt <= today)))) return null;
              } else if (activeJobsTab === "past") {
                if (!(endAt && endAt < today)) return null;
              } else if (activeJobsTab === "future") {
                if (!startAt || startAt <= today) return null;
              }

              return (
                <div key={index} onClick={() => navigate(`/job/${app.job_id}`)} className="rounded-lg p-4 outline outline-1 outline-gray-200 hover:outline-2 hover:outline-blue-600 cursor-pointer transition-colors">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {app.job_title}
                      </h4>
                      <p className="text-sm text-gray-700">
                        {app.company_name}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        Applied on {formatHumanReadable(app.created_at, true)}
                      </p>
                    </div>
                    {isLoadingInterviewStatus ? (
                      <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <>
                        <div className={`flex items-center gap-1 text-sm px-2 py-1 rounded-full ${getApplicationStatusClass(app.status)}`}>
                          {getStatusType(app.status) === "success" ? (
                            <CheckCircleIcon size={14} />
                          ) : getStatusType(app.status) === "rejected" ? (
                            <XCircleIcon size={14} />
                          ) : (
                            <ClockIcon size={14} />
                          )}
                          <span>{app.status}</span>
                        </div>
                      </>
                    )}
                  </div>
                  {
                    app.additional_notes && (
                      <p className="text-xs text-gray-600 mt-2 line-clamp-2">
                        {app.additional_notes}
                      </p>
                    )
                  }
                </div>
              )
            })}
          </>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No applications found. Try applying to more jobs to see them here.
          </div>
        )}
      </div>
      {/* {applications.length > 0 &&
        metadata.pagination.total_pages !== currentPage && (
          <div className="mt-6 flex justify-center">
            <Button
              variant="outline"
              className="text-sm bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
              onClick={handleLoadMore}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin h-4 w-4 border-2 border-gray-500 border-t-transparent rounded-full"></div>
                  Loading...
                </div>
              ) : (
                "Load More Applications"
              )}
            </Button>
          </div>
        )} */}
    </div >
  );
}
